import React, { FC, useEffect, useMemo, useState } from 'react';
import { object, string } from 'yup';
import { CustomObjectModelInput } from '@utmost.co/enterprise-models';
import { Dialog, AppForm, Button } from '@beeline.com/bp-design-system';
import { useTypedMutation } from 'src/utils/hooks/useTypedMutation';
import {
  CustomObjectCreationModelInput,
  CustomObjectUpdateModelInput,
  EntityStatusModel,
  StatusModel
} from 'src/graphql/types';
import { CreateCustomObjectDocument } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/graphql/__generated__/CreateCustomObject.graphql';
import { useCustomObjectValuesIntl } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/translations/useCustomObjectValuesIntl';
import { useStatusOptions } from '@oc/pages/settings/SpendSettings/FinancialFields/impl/utils/useStatusOptions';
import { CustomObject } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/CustomObject.types';
import { UpdateCustomObjectDocument } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/graphql/__generated__/UpdateCustomObject.graphql';
interface Props {
  onCloseModal: () => void;
  onCreate: () => void;
  onEdit: () => void;
  isOpen: boolean;
  isEdit: boolean;
  model: CustomObject | undefined;
  customObjectDefinitionUid: string | undefined;
}
export const CreateEditCustomObjectValueDialog: FC<React.PropsWithChildren<Props>> = ({
  onCloseModal,
  onCreate,
  onEdit,
  isOpen,
  isEdit,
  model,
  customObjectDefinitionUid
}) => {
  const intl = useCustomObjectValuesIntl();
  const statusOptions = useStatusOptions();
  const [modelInput, setModelInput] = useState<Partial<CustomObjectModelInput>>({
    name: '',
    description: '',
    status: StatusModel.Active,
    referenceId: '',
    referenceCode: ''
  });
  const evaluatedStatus = isEdit
    ? model?.status === StatusModel.Active
      ? StatusModel.Active
      : StatusModel.Inactive
    : StatusModel.Active;
  useEffect(() => {
    setModelInput({
      uid: model?.uid ?? '',
      name: model?.name ?? '',
      description: model?.description ?? '',
      status: evaluatedStatus,
      referenceId: model?.referenceId ?? '',
      referenceCode: model?.referenceCode ?? ''
    });
  }, [model, evaluatedStatus]);
  const { set: addNewObjectValue } = useTypedMutation(CreateCustomObjectDocument, {
    variables: {
      model: {
        customObjectDefinitionUid: customObjectDefinitionUid,
        name: modelInput.name,
        description: modelInput.description,
        status: modelInput.status === StatusModel.Active ? EntityStatusModel.Active : EntityStatusModel.Inactive,
        referenceId: modelInput.referenceId,
        referenceCode: modelInput.referenceCode
      }
    }
  });
  const { set: updateCustomObject } = useTypedMutation(UpdateCustomObjectDocument, {
    variables: {
      model: {
        uid: modelInput.uid,
        name: modelInput.name,
        description: modelInput.description,
        status: modelInput.status === StatusModel.Active ? EntityStatusModel.Active : EntityStatusModel.Inactive,
        referenceId: modelInput.referenceId,
        referenceCode: modelInput.referenceCode
      }
    }
  });
  const handleClose = () => {
    onCloseModal();
  };
  const handleSubmit = async () => {
    const response = isEdit ? await updateCustomObject() : await addNewObjectValue();
    if (!response.errors) {
      isEdit ? onEdit() : onCreate();
      onCloseModal();
    }
  };
  const validationSchema = useMemo(() => {
    const editValidation = object<Partial<CustomObjectUpdateModelInput>>({
      name: string().required(),
      referenceId: string().required()
    });
    const createValidation = object<Partial<CustomObjectCreationModelInput>>({
      name: string().required(),
      referenceId: string().required()
    });
    return isEdit ? editValidation : createValidation;
  }, [isEdit]);
  const checkIfFormIsValid = (customObject: Partial<CustomObjectModelInput> | undefined): boolean => {
    return !!customObject?.name && !!customObject.referenceId;
  };
  return (
    <Dialog
      title={isEdit ? intl.editCustomObject : intl.createCustomObject}
      onClose={handleClose}
      open={isOpen}
      show={{ cancelButton: true }}
      disableBackdropClick
      actions={[
        <Button
          variant="contained"
          color="primary"
          key="confirm-create-edit-custom-object-value_button"
          onClick={handleSubmit}
          disabled={!checkIfFormIsValid(modelInput)}
          dataTest="confirm-create-edit-custom-object-value_button"
        >
          {isEdit ? intl.editCustomObject : intl.createCustomObject}
        </Button>
      ]}
    >
      <AppForm onChange={setModelInput} initialValues={modelInput} validationSchema={validationSchema}>
        {({ setFieldTouched, setFieldValue }) => (
          <>
            <AppForm.Control name="name">
              <AppForm.Label required>{intl.name}</AppForm.Label>
              <AppForm.Input dataTest="custom-object-value-name" placeholder={intl.name} autoFocus />
              <AppForm.HelperText />
            </AppForm.Control>
            <AppForm.Control name="description">
              <AppForm.Label>{intl.description}</AppForm.Label>
              <AppForm.Input dataTest="custom-object-value-desc" placeholder={intl.description} />
              <AppForm.HelperText />
            </AppForm.Control>
            <AppForm.Control name="status">
              <AppForm.Label>{intl.status}</AppForm.Label>
              <AppForm.ChipsSelect
                options={statusOptions}
                onSelect={(value) => {
                  // @ts-ignore
                  setFieldValue('status', (value as StatusModel) ?? StatusModel.Active);
                  // @ts-ignore
                  setFieldTouched('status', true);
                }}
              />
            </AppForm.Control>
            <AppForm.Control name="referenceId">
              <AppForm.Label>{intl.referenceId}</AppForm.Label>
              <AppForm.Input dataTest="custom-object-value-referenceId" placeholder={intl.referenceId} />
              <AppForm.HelperText />
            </AppForm.Control>
            <AppForm.Control name="referenceCode">
              <AppForm.Label>{intl.referenceCode}</AppForm.Label>
              <AppForm.Input dataTest="custom-object-value-referenceCode" placeholder={intl.referenceCode} />
              <AppForm.HelperText />
            </AppForm.Control>
          </>
        )}
      </AppForm>
    </Dialog>
  );
};
